import {
  GridColumns,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { settings } from "../../../../../../customize/settings";
import { IDettaglio } from "../../../../../../interfaces/dettaglio/models";
import { IDettaglioTreeNode } from "../interfaces";
import { CellTreeNode, CellVenduto } from "../tableCells";
import { ICreateHistoryGridColumnsProps } from "./interfaces";

const i18_PATH = "prodotto.view.lottoDettagli.grids.historyGridColumns.";

const createAllColumns = ({
  t,
  onOpen,
  onClose,
  openList,
}: ICreateHistoryGridColumnsProps): GridColumns<IDettaglioTreeNode> => {
  const columns: GridColumns<IDettaglioTreeNode> = [
    {
      field: "depth",
      headerName: t(`${i18_PATH}Tipo`) ?? undefined,
      valueGetter: (
        params: GridValueGetterParams<unknown, IDettaglioTreeNode>
      ) => params?.row,
      renderCell: (params: GridRenderCellParams<IDettaglioTreeNode>) => (
        <CellTreeNode
          id={params.id as number}
          leaf={params.value?.leaf}
          depth={params.value?.depth}
          tipo={params.value?.tipo_lotto.nome}
          onOpen={onOpen}
          onClose={onClose}
          isOpen={openList.includes(params.id as number)}
        />
      ),
      sortable: false,
      width: 200,
    },
    {
      field: "suffissoCodice",
      headerName: "",
      sortable: false,
      width: 10,
    },
    {
      field: "magazzino",
      headerName: t(`${i18_PATH}Magazzino`) ?? undefined,
      valueGetter: (params: GridValueGetterParams<unknown, IDettaglio>) =>
        params?.row?.magazzino?.nome,
      sortable: false,
      minWidth: 120,
      flex: 1,
    },
    {
      field: "zona",
      headerName: t(`${i18_PATH}Zona`) ?? undefined,
      valueGetter: (params: GridValueGetterParams<unknown, IDettaglio>) =>
        params?.row?.zona?.nome,
      sortable: false,
      minWidth: 80,
      flex: 1,
    },
    {
      field: "fila",
      headerName: t(`${i18_PATH}Fila`) ?? undefined,
      valueGetter: (params: GridValueGetterParams<unknown, IDettaglio>) =>
        params?.row?.fila?.nome,
      sortable: false,
      width: 80,
    },
    {
      field: "bundle",
      headerName: t(`${i18_PATH}Bundle`) ?? undefined,
      type: "number",
      sortable: false,
      renderCell: (params: GridRenderCellParams<string>) => (
        <b>{params.formattedValue}</b>
      ),
      width: 60,
    },
    {
      field: "daLastra",
      headerName: t(`${i18_PATH}Da`) ?? undefined,
      type: "number",
      sortable: false,
      valueGetter: (params: GridValueGetterParams<undefined, IDettaglio>) =>
        params?.row?.tipo_lotto?.nome === "Lastre"
          ? params?.row?.lastreDa
          : "-------",
      width: 60,
    },
    {
      field: "aLastra",
      headerName: t(`${i18_PATH}A`) ?? undefined,
      type: "number",
      sortable: false,
      valueGetter: (params: GridValueGetterParams<undefined, IDettaglio>) =>
        params?.row?.tipo_lotto?.nome === "Lastre"
          ? params?.row?.lastreA
          : "-------",
      width: 60,
    },
    {
      field: "pezzi",
      headerName: t(`${i18_PATH}Pezzi`) ?? undefined,
      type: "number",
      sortable: false,
      width: 60,
    },
    {
      field: "misX",
      headerName: t(`${i18_PATH}Lun`) ?? undefined,
      type: "number",
      sortable: false,
      width: 80,
    },
    {
      field: "misY",
      headerName: t(`${i18_PATH}Alt`) ?? undefined,
      type: "number",
      sortable: false,
      width: 80,
    },
    {
      field: "misZ",
      headerName: t(`${i18_PATH}Spes`) ?? undefined,
      type: "number",
      sortable: false,
      width: 80,
    },
    {
      field: "quantitaUM",
      headerName: t(`${i18_PATH}Quantità`) ?? undefined,
      type: "number",
      sortable: false,
      renderCell: (params: GridRenderCellParams<number>) => {
        const value = parseFloat(String(params.value).replaceAll(",", "."));
        return Number(value.toFixed(2)).toLocaleString("it-IT");
      },
      width: 80,
    },
    {
      field: "um",
      headerName: "",
      valueGetter: (params: GridValueGetterParams<undefined, IDettaglio>) =>
        params?.value ? String(params.value) : "",
      sortable: false,
      width: 15,
    },
    {
      field: "costo",
      headerName: t(`${i18_PATH}Costo`) ?? undefined,
      type: "number",
      valueGetter: (params: GridValueGetterParams<undefined, IDettaglio>) => {
        const costiDiretti = params?.row?.costiDiretti || 0;
        const costiPadre = params?.row?.costiPadre || 0;
        const quantity = params?.row?.quantitaUM || 1;
        return (costiDiretti + costiPadre) / quantity;
      },
      renderCell: (params: GridRenderCellParams<number>) => {
        const value = parseFloat(String(params.value).replaceAll(",", "."));
        return Number(value)
          ? Number(value.toFixed(2)).toLocaleString("it-IT")
          : "";
      },
      sortable: false,
      width: 120,
    },
    {
      field: "um_costo",
      headerName: "",
      valueGetter: (params: GridValueGetterParams<undefined, IDettaglio>) =>
        params?.row?.um ? String(params?.row?.um) : "",
      sortable: false,
      width: 15,
    },
    {
      field: "prezzo",
      headerName: t(`${i18_PATH}Prezzo`) ?? undefined,
      type: "number",
      sortable: false,
      valueGetter: (params: GridValueGetterParams<undefined, IDettaglio>) =>
        params?.row?.prezzo,
      renderCell: (params: GridRenderCellParams<number>) => {
        const value = parseFloat(String(params.value).replaceAll(",", "."));
        return Number(value)
          ? Number(value.toFixed(2)).toLocaleString("it-IT")
          : "";
      },
      width: 120,
    },
    {
      field: "um_prezzo",
      headerName: "",
      sortable: false,
      valueGetter: (params: GridValueGetterParams<undefined, IDettaglio>) =>
        params?.row?.um ? String(params?.row?.um) : "",
      width: 15,
    },
    {
      field: "stato_lavorazione",
      headerName: t(`${i18_PATH}Lavorazione`) ?? undefined,
      valueGetter: (params: GridValueGetterParams<unknown, IDettaglio>) =>
        params?.row?.stato_lavorazione?.nome,
      sortable: false,
      minWidth: 120,
      flex: 1,
    },
    {
      field: "venduto",
      headerName: t(`${i18_PATH}Venduto`) ?? undefined,
      type: "actions",
      renderCell: (params: GridRenderCellParams<string>) =>
        params?.row.leaf ? (
          <CellVenduto value={parseInt(String(params.formattedValue))} />
        ) : (
          ""
        ),
      sortable: false,
    },
  ];
  const columnsToHide = settings.item?.grid?.historyColumnsToHide ?? [];
  return columns.filter((col) => !columnsToHide.includes(col.field));
};

export const createHistoryGridColumns = (
  props: ICreateHistoryGridColumnsProps
): GridColumns<IDettaglioTreeNode> => {
  const { canViewCommercials, canViewStoragePlace, canViewCosts } = props;
  let columns = createAllColumns(props);
  if (!canViewCosts)
    columns = columns.filter(
      (col) => col.field !== "costo" && col.field !== "um_costo"
    );
  if (!canViewCommercials)
    columns = columns.filter(
      (col) => col.field !== "prezzo" && col.field !== "um_prezzo"
    );
  if (!canViewStoragePlace)
    columns = columns.filter(
      (col) =>
        col.field !== "magazzino" &&
        col.field !== "zona" &&
        col.field !== "fila"
    );
  return columns;
};
